import type { PayloadAction } from '@reduxjs/toolkit';
import type { EntityDefinitions } from '@/types/eds/Definitions';
import type { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import type { DataTableColumnDefinition, DataTableRow } from '@/features/dataTable/types';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
  DataTableCreateRowPayload,
  DataTableDeleteRowPayload,
  DataTableFetchPayload,
  createDataTableRow,
  createDataTableRowFailed,
  createDataTableRowSuccess,
  deleteDataTableRow,
  deleteDataTableRowFailed,
  deleteDataTableRowSuccess,
  fetchDataTable,
  fetchDataTableFailed,
  fetchDataTableSuccess,
} from '@/features/dataTable/store/dataTableSlice';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import { edsToCodeFormat } from '@/features/eds/services/utils/edsToCodeFormat';
import { edsToEntityFormat } from '@/features/eds/services/utils/edsToCodeFormat';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';

type DataTableApiResponse = {
  listings: Array<Record<string, unknown> & { id: number | string; customFields?: Record<string, unknown> }>;
  pagination: {
    numberOfItems: number;
    page: number;
    perPage: number;
  };
};

function* fetchDataTableSaga({ payload }: PayloadAction<DataTableFetchPayload>) {
  try {
    const {
      dataTableName,
      parentEntityType,
      parentEntityId,
      pageNumber = 1,
      pageSize = 100,
      sortColumn,
      sortDirection = 'ASC',
    } = payload;

    const entityCode = (EdsEntityCodes as Record<string, string>)[dataTableName] || dataTableName;

    const entityDefinition: EntityDefinitions | null = yield call(
      EdsAdapterService.getEntityDefinition,
      entityCode as EdsEntityCodes,
      true,
    );

    const queryParams: Record<string, string | number> = {
      page: pageNumber,
      perPage: pageSize,
    };

    if (parentEntityType && parentEntityId !== undefined && parentEntityId !== null) {
      queryParams.parentEntityName = parentEntityType;
      queryParams.parentEntityKey = String(parentEntityId);
    }

    if (sortColumn) {
      queryParams.sortBy = sortColumn;
      queryParams.sortDirection = sortDirection;
    }

    const requestUrl = `/DataTables/${dataTableName}?${QueryParamsService.constructSearchParamsFromRecord(queryParams)}`;

    const response: { data: DataTableApiResponse } = yield call(HttpService.get, requestUrl, undefined, {
      baseURL: import.meta.env.VITE_API_URL,
    });

    const data = response.data;

    let columns: DataTableColumnDefinition[] = [];
    let rows: DataTableRow[] = [];

    // Build columns from entity definition first
    const entityDefColumns = buildColumnsFromEntityDefinition(entityDefinition);

    // Check if we have data rows
    if (data.listings && data.listings.length > 0) {
      // Step 4a: Use EdsAdapterService to normalize the response - this handles:
      // - Using cached entity definitions from step 1
      // - Processing customFields
      // - Building proper field metadata including displayValue for dates
      const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings as any, {
        includeId: true,
        forceUpdate: false, // Don't force update since we already fetched in step 1
      });

      // Build columns from normalized response as well
      const normalizedColumns = buildColumnsFromNormalizedResponse(normalizedResponse[0]);

      // Merge columns: use entity definition as base, add any missing from normalized response
      const entityDefColumnNames = new Set(entityDefColumns.map((c) => c.columnName));
      const additionalColumns = normalizedColumns.filter((c) => !entityDefColumnNames.has(c.columnName));
      columns = [...entityDefColumns, ...additionalColumns];

      // If still no columns, something is wrong
      if (columns.length === 0) {
        columns = normalizedColumns;
      }

      // Get column names for row extraction (only columns that are defined)
      const columnNames = new Set(columns.map((col) => col.columnName));

      // Transform response to DataTableRow format
      // Try to get values from normalized fields first, then fall back to raw data
      rows = normalizedResponse.map((item, index) => {
        const rawItem = data.listings[index];
        const rowData: DataTableRow = { id: item.id || rawItem?.id };

        // First, extract values from normalized fields
        Object.entries(item.fields).forEach(([fieldName, field]) => {
          if (field && columnNames.has(fieldName)) {
            rowData[fieldName] = field.value;
          }
        });

        // Also check virtualFields for any values that weren't in the entity definition
        if (item.virtualFields) {
          Object.entries(item.virtualFields).forEach(([fieldName, value]) => {
            const camelCaseFieldName = edsToCodeFormat(fieldName);
            if (columnNames.has(camelCaseFieldName) && !(camelCaseFieldName in rowData)) {
              rowData[camelCaseFieldName] = value;
            }
          });
        }

        // Fall back to raw data for any missing values
        // This handles cases where normalization doesn't capture all fields
        if (rawItem) {
          columnNames.forEach((colName) => {
            if (!(colName in rowData) || rowData[colName] === undefined || rowData[colName] === null) {
              // Try camelCase field name from raw data
              if (colName in rawItem) {
                rowData[colName] = rawItem[colName];
              }
              // Try PascalCase field name from raw data
              const pascalCaseFieldName = edsToEntityFormat(colName);
              if (pascalCaseFieldName in rawItem) {
                rowData[colName] = rawItem[pascalCaseFieldName];
              }
            }
          });
        }

        return rowData;
      });
    } else {
      // No data rows - use entity definition columns only
      columns = entityDefColumns;
    }

    yield put(
      fetchDataTableSuccess({
        rows,
        columns,
        totalCount: data?.pagination?.numberOfItems || rows.length,
      }),
    );
  } catch (error) {
    console.error('Failed to fetch data table rows:', error);
    yield put(fetchDataTableFailed('Failed to load data. Please try again.'));
  }
}

/**
 * Builds column definitions from normalized response fields.
 * Only includes fields that have definitions (not virtualFields).
 * Sorted by sequenceNumber (displayOrder from definition).
 */
function buildColumnsFromNormalizedResponse(normalizedItem?: NormalizedV2Response): DataTableColumnDefinition[] {
  if (!normalizedItem) return [];

  const columns: DataTableColumnDefinition[] = [];

  // Build columns ONLY from normalized fields (these have proper metadata from definitions)
  // Do NOT include virtualFields - they are unknown fields without definition
  Object.entries(normalizedItem.fields).forEach(([fieldName, field]) => {
    if (!field) return;

    // Skip hidden fields (isDisplay === false means the field should be hidden)
    if (field.isDisplay === false) return;

    // Extract allowedValues from validation if present
    const allowedValues = field.validation?.allowedValues
      ?.filter((opt) => opt.value !== null)
      .map((opt) => ({
        label: String(opt.label),
        value: opt.value as string | number | boolean,
      }));

    // Extract metadata (prefix/postfix for currency, etc.)
    const metadata = field.metadata
      ? {
          prefix: (field.metadata as { prefix?: string }).prefix,
          postfix: (field.metadata as { postfix?: string }).postfix,
        }
      : undefined;

    columns.push({
      columnName: fieldName,
      // Convert camelCase back to PascalCase for API updates
      // e.g., slaDays -> SlaDays
      propertyName: edsToEntityFormat(fieldName),
      label: field.displayTitle || formatColumnLabel(fieldName),
      dataType: field.dataType || 'String',
      isEditable: field.isEditable !== false,
      isRequired: (field.validation as { required?: boolean })?.required,
      isSortable: field.isSortable,
      isFilterable: field.isFilterable,
      sequenceNumber: field.sequenceNumber,
      allowedValues: allowedValues?.length ? allowedValues : undefined,
      metadata: metadata?.prefix || metadata?.postfix ? metadata : undefined,
    });
  });

  // Sort columns by sequenceNumber (displayOrder from definition)
  columns.sort((a, b) => {
    const seqA = a.sequenceNumber ?? Number.MAX_SAFE_INTEGER;
    const seqB = b.sequenceNumber ?? Number.MAX_SAFE_INTEGER;
    return seqA - seqB;
  });

  return columns;
}

/**
 * Builds column definitions from entity definition (render endpoint).
 * Used when there are no data rows to build columns from.
 * This ensures the Add Row form works even when the table is empty.
 */
function buildColumnsFromEntityDefinition(entityDefinition: EntityDefinitions | null): DataTableColumnDefinition[] {
  if (!entityDefinition || !entityDefinition.fields) return [];

  const columns: DataTableColumnDefinition[] = [];

  Object.entries(entityDefinition.fields).forEach(([fieldName, field]) => {
    if (!field) return;

    // Skip hidden fields (isDisplay === false means the field should be hidden)
    if (field.isDisplay === false) return;

    // Convert fieldName from PascalCase to camelCase for consistency
    const columnName = edsToCodeFormat(fieldName);

    // Extract allowedValues from validation if present
    const allowedValues = field.validation?.allowedValues
      ?.filter((opt) => opt.value !== null)
      .map((opt) => ({
        label: String(opt.label),
        value: opt.value as string | number | boolean,
      }));

    columns.push({
      columnName,
      propertyName: fieldName, // Keep original PascalCase for API
      label: field.displayTitle || formatColumnLabel(fieldName),
      dataType: field.dataType || 'String',
      isEditable: field.isEditable !== false,
      isRequired: (field.validation as { required?: boolean })?.required,
      isSortable: field.isSortable,
      isFilterable: field.isFilterable,
      sequenceNumber: field.sequenceNumber,
      allowedValues: allowedValues?.length ? allowedValues : undefined,
    });
  });

  // Sort columns by sequenceNumber
  columns.sort((a, b) => {
    const seqA = a.sequenceNumber ?? Number.MAX_SAFE_INTEGER;
    const seqB = b.sequenceNumber ?? Number.MAX_SAFE_INTEGER;
    return seqA - seqB;
  });

  return columns;
}

/**
 * Formats a camelCase or PascalCase column name to a readable label
 */
function formatColumnLabel(columnName: string): string {
  return columnName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

/**
 * Saga for deleting a data table row
 * Uses endpoint: DELETE /api/v1/DataTables/delete-row/{dataTableName}/{rowId}
 */
function* deleteDataTableRowSaga({ payload }: PayloadAction<DataTableDeleteRowPayload>) {
  try {
    const { dataTableName, rowId } = payload;

    // Call the delete endpoint
    // Endpoint: DELETE /api/v1/DataTables/delete-row/{dataTableName}/{rowId}
    yield call(HttpService.delete, `/DataTables/delete-row/${dataTableName}/${rowId}`, {
      baseURL: import.meta.env.VITE_API_URL,
    });

    yield put(deleteDataTableRowSuccess(rowId));
  } catch (error) {
    console.error('Failed to delete data table row:', error);
    yield put(deleteDataTableRowFailed('Failed to delete row. Please try again.'));
  }
}

/**
 * Saga for creating a new data table row
 * Uses endpoint: POST /api/v1/DataTables/create-row
 * Request body: { dataTableName, parentEntityType, parentEntityKey, properties }
 */
function* createDataTableRowSaga({ payload }: PayloadAction<DataTableCreateRowPayload>) {
  try {
    const { dataTableName, parentEntityType, parentEntityKey, properties } = payload;

    // Call the create endpoint
    // Endpoint: POST /api/v1/DataTables/create-row
    yield call(
      HttpService.post,
      `/DataTables/create-row`,
      {
        dataTableName,
        ...(parentEntityType ? { parentEntityType } : {}),
        ...(parentEntityKey !== undefined && parentEntityKey !== null
          ? { parentEntityKey: Number(parentEntityKey) }
          : {}),
        properties,
      },
      {
        baseURL: import.meta.env.VITE_API_URL,
      },
    );

    // After successful creation, re-fetch the data table to get proper IDs and all fields
    // This ensures we have the real row ID from the backend for subsequent edits
    yield put(
      fetchDataTable({
        dataTableName,
        parentEntityType,
        parentEntityId: parentEntityKey,
      }),
    );

    // Mark creation as successful (the fetchDataTable will update the rows)
    yield put(createDataTableRowSuccess({ id: -1 })); // Dummy row, will be replaced by fetch
  } catch (error) {
    console.error('Failed to create data table row:', error);
    yield put(createDataTableRowFailed('Failed to create row. Please try again.'));
  }
}

export function* dataTableWatcher() {
  yield takeLatest(fetchDataTable.type, fetchDataTableSaga);
  yield takeLatest(deleteDataTableRow.type, deleteDataTableRowSaga);
  yield takeLatest(createDataTableRow.type, createDataTableRowSaga);
}
